import { useEffect, useReducer, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { format } from 'date-fns'

import Modal from './Modal'
import Button from './Button'
import Loader from './Loader'
import { partnerControl, controlResult, isAuthError } from '../api/control'
import { useMount } from '../hooks/use-mount'
import { useNotificationContext } from '../context/notification-context'
import {
  BUILD_ENV,
  classNames,
  downloadJson,
  extractErrorMessage,
  fileTimestamp,
  formatToCurrency,
  sleep
} from '../helper'
import {
  BLOCK_REASONS,
  COLLECTION_LABELS,
  DELETE_PARTNER_COLLECTIONS,
  FATES,
  MAX_CONSECUTIVE_ERRORS,
  MAX_ITERATIONS,
  THROTTLE_BACKOFF_MS,
  buildDeleteConsequences
} from '../constants/delete-partner'

// ─── Helfer ────────────────────────────────────────────────────────────────

const COLLECTION_KEYS = DELETE_PARTNER_COLLECTIONS.map(({ key }) => key)

// Summiert nur die Collection-Keys — preview.counts trägt zusätzlich
// Detailzähler (talentSpacesDemo etc.), die nicht doppelt zählen dürfen.
const sumCollections = (obj) =>
  COLLECTION_KEYS.reduce(
    (sum, key) => (typeof obj?.[key] === 'number' ? sum + obj[key] : sum),
    0
  )

const addCounts = (a, b) => {
  const out = { ...a }
  Object.entries(b || {}).forEach(([key, value]) => {
    if (typeof value === 'number') out[key] = (out[key] || 0) + value
  })
  return out
}

const errorKey = (e) =>
  `${e.step || e.collection || ''}:${e.id || ''}:${e.message}`

const mergeErrors = (prev, next) => {
  const seen = new Set(prev.map(errorKey))
  const out = [...prev]
  ;(next || []).forEach((e) => {
    const key = errorKey(e)
    if (seen.has(key)) return
    seen.add(key)
    out.push(e)
  })
  return out
}

const appendLog = (log, entry) => [...log, entry].slice(-200)

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const formatDate = (value) => {
  try {
    return value ? format(new Date(value), 'dd.MM.yyyy') : '–'
  } catch (e) {
    return String(value)
  }
}

const formatCount = (value) => (typeof value === 'number' ? value : '–')

// ─── State ─────────────────────────────────────────────────────────────────

const initialState = {
  // loadingPreview | previewFailed | preview | confirming | deleting | paused | failed | done
  phase: 'loadingPreview',
  preview: null,
  previewError: null,
  confirmText: '',
  acknowledged: {},
  backup: null,
  iterations: 0,
  processed: {},
  remaining: null,
  errors: [],
  log: [],
  pauseReason: null,
  fatalError: null
}

function reducer(state, action) {
  switch (action.type) {
    case 'PREVIEW_START':
      return { ...initialState, phase: 'loadingPreview' }
    case 'PREVIEW_OK':
      return { ...state, phase: 'preview', preview: action.preview }
    case 'PREVIEW_FAIL':
      return { ...state, phase: 'previewFailed', previewError: action.message }
    case 'GO_CONFIRM':
      return {
        ...state,
        phase: 'confirming',
        confirmText: '',
        acknowledged: {}
      }
    case 'BACK_TO_PREVIEW':
      return { ...state, phase: 'preview' }
    case 'SET_CONFIRM_TEXT':
      return { ...state, confirmText: action.value }
    case 'TOGGLE_ACK':
      return {
        ...state,
        acknowledged: {
          ...state.acknowledged,
          [action.key]: !state.acknowledged[action.key]
        }
      }
    case 'DELETE_START':
      return {
        ...state,
        phase: 'deleting',
        backup: action.backup ?? state.backup,
        pauseReason: null,
        fatalError: null
      }
    case 'ITERATION_OK': {
      const { processed = {}, remaining = null, errors = [] } = action.res
      const iteration = state.iterations + 1
      const processedNow = sumCollections(processed)
      const remainingNow = remaining ? sumCollections(remaining) : null
      return {
        ...state,
        iterations: iteration,
        remaining: remaining ?? state.remaining,
        processed: addCounts(state.processed, processed),
        errors: mergeErrors(state.errors, errors),
        log: appendLog(state.log, {
          iteration,
          ok: true,
          text: `${processedNow} Datensätze verarbeitet${
            remainingNow !== null ? `, ${remainingNow} verbleibend` : ''
          }${action.res.throttled ? ' – DynamoDB drosselt, kurze Pause' : ''}`
        })
      }
    }
    case 'ITERATION_ERROR': {
      const iteration = state.iterations + 1
      return {
        ...state,
        iterations: iteration,
        log: appendLog(state.log, {
          iteration,
          ok: false,
          text: action.message
        })
      }
    }
    case 'PAUSE':
      return { ...state, phase: 'paused', pauseReason: action.reason }
    case 'DONE':
      return { ...state, phase: 'done', remaining: {} }
    case 'FAIL':
      return { ...state, phase: 'failed', fatalError: action.message }
    default:
      return state
  }
}

// ─── Teil-Ansichten ────────────────────────────────────────────────────────

const STEPS = [
  {
    key: 'preview',
    label: '1 Vorschau',
    phases: ['loadingPreview', 'previewFailed', 'preview']
  },
  { key: 'confirm', label: '2 Bestätigen', phases: ['confirming'] },
  {
    key: 'delete',
    label: '3 Löschen',
    phases: ['deleting', 'paused', 'failed', 'done']
  }
]

const StepBadges = ({ phase }) => (
  <ol className='flex gap-2' aria-label='Schritte'>
    {STEPS.map((step) => {
      const active = step.phases.includes(phase)
      return (
        <li
          key={step.key}
          className={classNames(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            active ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500'
          )}
          aria-current={active ? 'step' : undefined}
        >
          {step.label}
        </li>
      )
    })}
  </ol>
)

const FateBadge = ({ fate }) => {
  const config = FATES[fate] || FATES.DELETE
  return (
    <span
      className={classNames(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        config.className
      )}
    >
      {config.label}
    </span>
  )
}

const ErrorList = ({ errors }) => (
  <div className='mt-3'>
    <p className='text-sm font-medium text-red-700'>{errors.length} Fehler:</p>
    <ul className='mt-1 max-h-40 space-y-1 overflow-y-auto pl-1'>
      {errors.map((e) => (
        <li
          key={errorKey(e)}
          className='break-all rounded bg-red-50 p-2 font-mono text-xs text-red-700'
        >
          <span className='font-semibold'>
            {COLLECTION_LABELS[e.step] || e.step || e.collection || 'Schritt'}
            {e.id ? ` ${e.id}` : ''}:
          </span>{' '}
          {e.message}
        </li>
      ))}
    </ul>
  </div>
)

const BlockedPanel = ({ preview, partnerId, customerName }) => (
  <div
    role='alert'
    className='mt-4 rounded-md bg-red-50 p-4 ring-1 ring-inset ring-red-600/20'
  >
    <h3 className='text-sm font-semibold text-red-800'>
      Löschen nicht möglich
    </h3>
    <p className='mt-1 text-sm text-red-700'>
      {BLOCK_REASONS[preview.reason] || preview.reason}
    </p>
    {preview.reason === 'ACTIVE_PAID_PAYMENT' && (
      <ul className='mt-3 divide-y divide-red-100'>
        {(preview.blockingPayments || []).map((p) => (
          <li
            key={p.id}
            className='flex items-center justify-between gap-4 py-2 text-sm text-red-800'
          >
            <span>
              gültig bis {formatDate(p.expires_at)} · Status {p.status || '–'} ·{' '}
              {typeof p.price === 'number'
                ? formatToCurrency((p.price / 100) * 12)
                : '–'}{' '}
              · {(p.products || []).join(', ') || '–'}
            </span>
            <Link
              to={`/customer/payment/${p.id}`}
              state={{ customerName, customerId: partnerId }}
              className='whitespace-nowrap font-medium underline'
            >
              Zahlung öffnen
            </Link>
          </li>
        ))}
      </ul>
    )}
  </div>
)

const PreviewTable = ({ counts }) => (
  <div className='mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg'>
    <table className='min-w-full divide-y divide-gray-300'>
      <thead className='bg-gray-50'>
        <tr>
          <th
            scope='col'
            className='py-2 pl-4 pr-3 text-left text-xs font-medium text-gray-500'
          >
            Daten
          </th>
          <th
            scope='col'
            className='px-3 py-2 text-right text-xs font-medium text-gray-500'
          >
            Anzahl
          </th>
          <th
            scope='col'
            className='px-3 py-2 text-left text-xs font-medium text-gray-500'
          >
            Was passiert
          </th>
        </tr>
      </thead>
      <tbody className='divide-y divide-gray-200 bg-white'>
        {DELETE_PARTNER_COLLECTIONS.map(({ key, label, fate }) => (
          <tr key={key}>
            <td className='py-2 pl-4 pr-3 text-sm text-gray-900'>{label}</td>
            <td className='px-3 py-2 text-right text-sm tabular-nums text-gray-900'>
              {formatCount(counts?.[key])}
            </td>
            <td className='px-3 py-2'>
              <FateBadge fate={fate} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const SampleList = ({ title, items, renderItem, truncated, total }) => {
  if (!items || items.length === 0) return null
  return (
    <div className='mt-5'>
      <h3 className='text-sm font-semibold text-gray-900'>
        {title}
        {truncated ? ` (${items.length} von ${total})` : ''}
      </h3>
      <ul className='mt-2 max-h-40 divide-y divide-gray-100 overflow-y-auto text-sm'>
        {items.map(renderItem)}
      </ul>
    </div>
  )
}

const ProgressTable = ({ preview, processed, remaining }) => (
  <div className='mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg'>
    <table className='min-w-full divide-y divide-gray-300'>
      <thead className='bg-gray-50'>
        <tr>
          <th
            scope='col'
            className='py-2 pl-4 pr-3 text-left text-xs font-medium text-gray-500'
          >
            Daten
          </th>
          <th
            scope='col'
            className='px-3 py-2 text-right text-xs font-medium text-gray-500'
          >
            Gesamt
          </th>
          <th
            scope='col'
            className='px-3 py-2 text-right text-xs font-medium text-gray-500'
          >
            Verarbeitet
          </th>
          <th
            scope='col'
            className='px-3 py-2 text-right text-xs font-medium text-gray-500'
          >
            Verbleibend
          </th>
        </tr>
      </thead>
      <tbody className='divide-y divide-gray-200 bg-white'>
        {DELETE_PARTNER_COLLECTIONS.map(({ key, label }) => (
          <tr key={key}>
            <td className='py-2 pl-4 pr-3 text-sm text-gray-900'>{label}</td>
            <td className='px-3 py-2 text-right text-sm tabular-nums text-gray-500'>
              {formatCount(preview?.counts?.[key])}
            </td>
            <td className='px-3 py-2 text-right text-sm tabular-nums text-gray-900'>
              {formatCount(processed?.[key] ?? 0)}
            </td>
            <td className='px-3 py-2 text-right text-sm tabular-nums text-gray-900'>
              {formatCount(remaining?.[key])}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

// ─── Ablauf ────────────────────────────────────────────────────────────────

function DeletePartnerFlow({ partner, close, onLockChange }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { success } = useNotificationContext()
  const { user } = useAuthenticator((context) => [context.user])
  const [state, dispatch] = useReducer(reducer, initialState)

  // Späte Antworten nach Unmount/Neustart ignorieren; busyRef verhindert
  // parallele Läufe (State ist asynchron, ein Doppelklick käme sonst durch).
  const runRef = useRef(0)
  const mountedRef = useRef(true)
  const busyRef = useRef(false)
  // Backend-Item-Fehler über alle Durchläufe (auch nach „Fortsetzen"). Nur
  // sie halten am Ende das Ergebnis-Panel offen — ein zwischenzeitlicher
  // Netzwerkfehler, der beim Wiederholen durchging, braucht keine Nacharbeit.
  const itemErrorsRef = useRef(0)

  useEffect(
    () => () => {
      mountedRef.current = false
      runRef.current += 1
    },
    []
  )

  useEffect(() => {
    onLockChange(state.phase === 'deleting')
  }, [state.phase, onLockChange])

  useEffect(() => {
    if (state.phase !== 'deleting') return undefined
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [state.phase])

  const loadPreview = async () => {
    const run = ++runRef.current
    dispatch({ type: 'PREVIEW_START' })
    try {
      const body = await partnerControl({
        action: 'previewDeletePartner',
        partner_id: partner.id
      })
      if (runRef.current !== run || !mountedRef.current) return
      dispatch({
        type: 'PREVIEW_OK',
        preview: controlResult(body, 'previewDeletePartner')
      })
    } catch (err) {
      if (runRef.current !== run || !mountedRef.current) return
      dispatch({ type: 'PREVIEW_FAIL', message: extractErrorMessage(err) })
    }
  }

  useMount(() => {
    loadPreview()
  })

  // ─── Abgeleitete Werte ───────────────────────────────────────────────────

  const { preview } = state
  const expected = preview?.partner?.name || partner.name || partner.id
  const customerName =
    preview?.partner?.display_name ||
    preview?.partner?.name ||
    partner.display_name ||
    partner.name ||
    partner.id
  const consequences = buildDeleteConsequences(
    preview?.counts,
    preview?.archive_window_days || 20
  )
  const openAcknowledgements = consequences.filter(
    (c) => !state.acknowledged[c.key]
  ).length
  const nameMatches = state.confirmText.trim() === expected
  const canDelete =
    preview?.deletable === true && openAcknowledgements === 0 && nameMatches
  const total = sumCollections(preview?.counts) + 1 // +1 = Partner-Datensatz
  const remainingTotal = state.remaining
    ? sumCollections(state.remaining)
    : null
  const processedTotal = sumCollections(state.processed)
  let pct = 0
  if (state.phase === 'done') pct = 100
  else if (remainingTotal !== null) {
    pct = clamp(Math.round(((total - remainingTotal) / total) * 100), 0, 99)
  } else pct = clamp(Math.round((processedTotal / total) * 100), 0, 99)

  // ─── Aktionen ────────────────────────────────────────────────────────────

  const leaveToList = () => {
    // Erst navigieren, dann die Detail-Queries entfernen: bei noch gemounteter
    // Detailseite würde ein Invalidate den gelöschten Partner sofort neu laden.
    navigate('/customers', { replace: true })
    queryClient.removeQueries(['customer', partner.id])
    queryClient.removeQueries(['customer-teammember', partner.id])
    queryClient.removeQueries(['payment', partner.id])
    queryClient.invalidateQueries(['customers'])
  }

  const buildBackup = () => ({
    filename: `partner-delete-backup-${partner.id}-${fileTimestamp()}.json`,
    data: {
      exportedAt: new Date().toISOString(),
      exportedBy: user?.signInUserSession?.idToken?.payload?.email || null,
      environment: BUILD_ENV,
      preview: state.preview
    }
  })

  const finish = (res) => {
    dispatch({ type: 'DONE' })
    success(
      res.notFound
        ? `Kunde ${customerName} war bereits gelöscht`
        : `Kunde ${customerName} wurde gelöscht`
    )
    if (itemErrorsRef.current === 0) leaveToList()
  }

  const runDeletion = async () => {
    busyRef.current = true
    const run = ++runRef.current
    const alive = () => runRef.current === run && mountedRef.current
    let consecutiveErrors = 0

    try {
      for (let i = 0; i < MAX_ITERATIONS; i++) {
        let res
        try {
          const body = await partnerControl({
            action: 'deletePartner',
            partner_id: partner.id
          })
          if (!alive()) return
          res = controlResult(body, 'deletePartner')
        } catch (err) {
          if (!alive()) return
          const message = extractErrorMessage(err)
          if (isAuthError(err)) {
            dispatch({ type: 'FAIL', message })
            return
          }
          consecutiveErrors += 1
          dispatch({ type: 'ITERATION_ERROR', message })
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            dispatch({ type: 'PAUSE', reason: 'CONSECUTIVE_ERRORS' })
            return
          }
          await sleep(1000 * 2 ** (consecutiveErrors - 1))
          if (!alive()) return
          continue
        }

        consecutiveErrors = 0
        itemErrorsRef.current += (res.errors || []).length
        dispatch({ type: 'ITERATION_OK', res })

        if (res.blocked) {
          dispatch({
            type: 'FAIL',
            message: BLOCK_REASONS[res.blocked.reason] || res.blocked.reason
          })
          return
        }
        if (res.done || res.notFound) {
          finish(res)
          return
        }
        if (res.throttled) {
          await sleep(THROTTLE_BACKOFF_MS)
          if (!alive()) return
        }
      }
      dispatch({ type: 'PAUSE', reason: 'MAX_ITERATIONS' })
    } finally {
      busyRef.current = false
    }
  }

  const startDeletion = () => {
    if (state.phase !== 'confirming' || !canDelete || busyRef.current) return
    const backup = buildBackup()
    downloadJson(backup)
    dispatch({ type: 'DELETE_START', backup })
    runDeletion()
  }

  const resumeDeletion = () => {
    if (!['paused', 'failed'].includes(state.phase) || busyRef.current) return
    dispatch({ type: 'DELETE_START' })
    runDeletion()
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  const header = (
    <div className='flex items-start justify-between gap-4'>
      <div className='min-w-0'>
        <h2 className='text-base font-semibold leading-6 text-gray-900'>
          Kunden löschen
        </h2>
        <p className='mt-1 truncate text-sm text-gray-500'>
          {customerName} ·{' '}
          <span className='font-mono text-xs'>{partner.id}</span>
        </p>
      </div>
      <StepBadges phase={state.phase} />
    </div>
  )

  if (state.phase === 'loadingPreview') {
    return (
      <div>
        {header}
        <Loader loadingText='Löschvorschau wird geladen ...' />
      </div>
    )
  }

  if (state.phase === 'previewFailed') {
    return (
      <div>
        {header}
        <div role='alert' className='mt-4 rounded-md bg-red-50 p-4'>
          <h3 className='text-sm font-semibold text-red-800'>
            Vorschau konnte nicht geladen werden
          </h3>
          <p className='mt-1 break-all font-mono text-xs text-red-700'>
            {state.previewError}
          </p>
        </div>
        <div className='mt-6 flex justify-end gap-3'>
          <Button.SecondaryLG text='Schließen' onClick={close} />
          <Button.PrimaryLG text='Erneut laden' onClick={loadPreview} />
        </div>
      </div>
    )
  }

  if (state.phase === 'preview') {
    const blocked = preview.deletable !== true
    return (
      <div>
        {header}
        {blocked && (
          <BlockedPanel
            preview={preview}
            partnerId={partner.id}
            customerName={customerName}
          />
        )}
        {preview.reason !== 'NOT_FOUND' && (
          <>
            <p className='mt-4 text-sm text-gray-600'>
              So wird der Kunde aufgelöst. Gelöschte Datensätze liegen danach
              noch {preview.archive_window_days || 20} Tage im Archive-Table.
            </p>
            <PreviewTable counts={preview.counts} />
            {preview.counts_partial && (
              <p className='mt-2 text-xs text-yellow-700'>
                Einzelne Zähler wurden gekappt und sind Mindestwerte.
              </p>
            )}
            {preview.errors?.length > 0 && (
              <p className='mt-2 text-xs text-yellow-700'>
                {preview.errors.length} Zähler konnten nicht ermittelt werden.
              </p>
            )}
            <SampleList
              title='Teammitglieder'
              items={preview.samples?.employees}
              truncated={preview.truncated?.employees}
              total={preview.counts?.employees}
              renderItem={(e) => (
                <li
                  key={e.space_id}
                  className='flex justify-between gap-4 py-1.5'
                >
                  <span className='truncate text-gray-900'>
                    {[e.firstname, e.lastname].filter(Boolean).join(' ') || '–'}
                  </span>
                  <span className='truncate text-gray-500'>
                    {e.email || e.owner}
                  </span>
                </li>
              )}
            />
            <SampleList
              title='Stellen'
              items={preview.samples?.careers}
              truncated={preview.truncated?.careers}
              total={preview.counts?.careers}
              renderItem={(c) => (
                <li key={c.id} className='flex justify-between gap-4 py-1.5'>
                  <span className='truncate text-gray-900'>
                    {c.name || c.id}
                  </span>
                  <span className='whitespace-nowrap text-gray-500'>
                    {c.is_demo ? 'Demo · ' : ''}
                    {c.status || '–'}
                  </span>
                </li>
              )}
            />
            <SampleList
              title='Integrationen'
              items={preview.samples?.integrations}
              truncated={preview.truncated?.integrations}
              total={preview.counts?.integrations}
              renderItem={(i) => (
                <li key={i.id} className='flex justify-between gap-4 py-1.5'>
                  <span className='text-gray-900'>
                    {i.remote_service || '–'}
                  </span>
                  <span className='truncate text-gray-500'>
                    {i.remote_service_name || i.id}
                  </span>
                </li>
              )}
            />
          </>
        )}
        <div className='mt-6 flex justify-end gap-3'>
          <Button.SecondaryLG text='Abbrechen' onClick={close} />
          {preview.reason === 'NOT_FOUND' ? (
            <Button.PrimaryLG text='Zur Kundenliste' onClick={leaveToList} />
          ) : (
            <Button.DangerLG
              text='Weiter zur Bestätigung'
              disabled={blocked}
              onClick={() => dispatch({ type: 'GO_CONFIRM' })}
            />
          )}
        </div>
      </div>
    )
  }

  if (state.phase === 'confirming') {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          startDeletion()
        }}
      >
        {header}
        <div
          role='alert'
          className='mt-4 rounded-md bg-red-50 p-4 ring-1 ring-inset ring-red-600/20'
        >
          <h3 className='text-sm font-semibold text-red-800'>
            Endgültige Löschung von {customerName}
          </h3>
          <p className='mt-1 text-sm text-red-700'>
            Du löschst <strong>{customerName}</strong> (
            <span className='font-mono text-xs'>{partner.id}</span>) mit {total}{' '}
            Datensätzen. Es gibt kein Rückgängig im Dashboard. Bestätige jeden
            Punkt einzeln und tippe danach den Namen des Kunden. Ein JSON-Backup
            der Vorschau wird beim Klick automatisch heruntergeladen.
          </p>
        </div>

        <fieldset className='mt-4'>
          <legend className='text-sm font-semibold text-gray-900'>
            Ich bestätige ausdrücklich:
          </legend>
          <ul className='mt-2 divide-y divide-gray-100 rounded-md ring-1 ring-inset ring-gray-200'>
            {consequences.map((c) => (
              <li key={c.key}>
                <label
                  htmlFor={`ack-${c.key}`}
                  className='flex cursor-pointer gap-3 px-3 py-3 hover:bg-gray-50'
                >
                  <input
                    id={`ack-${c.key}`}
                    type='checkbox'
                    checked={!!state.acknowledged[c.key]}
                    onChange={() =>
                      dispatch({ type: 'TOGGLE_ACK', key: c.key })
                    }
                    className='mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-red-600 focus:ring-red-600'
                  />
                  <span>
                    <span className='block text-sm font-medium text-gray-900'>
                      {c.title}
                    </span>
                    <span className='mt-0.5 block text-xs text-gray-600'>
                      {c.text}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        <label
          htmlFor='confirm-partner-name'
          className='mt-4 block text-sm font-medium text-gray-900'
        >
          Tippe{' '}
          <code className='rounded bg-gray-100 px-1 py-0.5 text-xs'>
            {expected}
          </code>{' '}
          zur Bestätigung
        </label>
        <input
          id='confirm-partner-name'
          type='text'
          autoFocus
          autoComplete='off'
          spellCheck={false}
          value={state.confirmText}
          onChange={(e) =>
            dispatch({ type: 'SET_CONFIRM_TEXT', value: e.target.value })
          }
          aria-describedby='confirm-partner-hint'
          className='mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-red-600 sm:text-sm sm:leading-6'
        />
        <p id='confirm-partner-hint' className='mt-1 text-xs text-gray-500'>
          Exakte Schreibweise inkl. Groß-/Kleinschreibung.
        </p>
        <p aria-live='polite' className='mt-3 text-xs text-gray-500'>
          {canDelete
            ? 'Alle Bestätigungen liegen vor. Der Button löscht endgültig.'
            : [
                openAcknowledgements > 0 &&
                  `${openAcknowledgements} von ${consequences.length} Bestätigungen offen`,
                !nameMatches && 'Name stimmt noch nicht überein'
              ]
                .filter(Boolean)
                .join(' · ')}
        </p>
        <div className='mt-6 flex justify-end gap-3'>
          <Button.SecondaryLG
            text='Zurück'
            onClick={() => dispatch({ type: 'BACK_TO_PREVIEW' })}
          />
          <Button.DangerLG
            type='submit'
            text='Endgültig löschen'
            disabled={!canDelete}
          />
        </div>
      </form>
    )
  }

  if (state.phase === 'deleting') {
    return (
      <div>
        {header}
        <div className='mt-4 flex items-center justify-between text-sm'>
          <span className='font-medium text-gray-900'>
            Kunde wird gelöscht …
          </span>
          <span className='tabular-nums text-gray-500'>{pct} %</span>
        </div>
        <div
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label='Löschfortschritt'
          className='mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200'
        >
          <div
            className='h-2 rounded-full bg-red-600 transition-all'
            style={{ width: `${pct}%` }}
          />
        </div>
        <p aria-live='polite' className='mt-2 text-xs text-gray-500'>
          Durchlauf {state.iterations} · {remainingTotal ?? '–'} Datensätze
          verbleibend · Fenster nicht schließen.
        </p>
        <ProgressTable
          preview={preview}
          processed={state.processed}
          remaining={state.remaining}
        />
        {state.log.length > 0 && (
          <ul className='mt-4 max-h-32 overflow-y-auto rounded-md bg-gray-50 p-2 font-mono text-xs'>
            {state.log.map((l) => (
              <li
                key={l.iteration}
                className={l.ok ? 'text-gray-700' : 'text-red-700'}
              >
                #{l.iteration} {l.text}
              </li>
            ))}
          </ul>
        )}
        {state.errors.length > 0 && <ErrorList errors={state.errors} />}
      </div>
    )
  }

  // paused | failed | done (mit Fehlern – ohne Fehler wurde bereits navigiert)
  const isPaused = state.phase === 'paused'
  const isFailed = state.phase === 'failed'
  const panelClass = isPaused
    ? 'bg-yellow-50'
    : isFailed
      ? 'bg-red-50'
      : 'bg-green-50'
  const titleClass = isPaused
    ? 'text-yellow-800'
    : isFailed
      ? 'text-red-800'
      : 'text-green-800'
  const title = isPaused
    ? 'Löschung unterbrochen'
    : isFailed
      ? 'Löschung fehlgeschlagen'
      : `Kunde gelöscht – ${state.errors.length} Fehler`
  const description = isPaused
    ? state.pauseReason === 'CONSECUTIVE_ERRORS'
      ? `${MAX_CONSECUTIVE_ERRORS} Netzwerk- oder Serverfehler in Folge. Der Vorgang ist wiederaufnehmbar; bereits gelöschte Daten werden übersprungen.`
      : `${MAX_ITERATIONS} Durchläufe erreicht. Der Vorgang ist wiederaufnehmbar; bereits gelöschte Daten werden übersprungen.`
    : isFailed
      ? state.fatalError
      : 'Der Partner-Datensatz wurde entfernt. Einzelne Datensätze konnten nicht verarbeitet werden – bitte prüfen.'

  return (
    <div>
      {header}
      <div className={classNames('mt-4 rounded-md p-4', panelClass)}>
        <h3 className={classNames('text-sm font-semibold', titleClass)}>
          {title}
        </h3>
        <p className='mt-1 break-words text-sm text-gray-700'>{description}</p>
        {state.iterations > 0 && (
          <p className='mt-2 text-xs text-gray-500'>
            {state.iterations} Durchläufe · {processedTotal} Datensätze
            verarbeitet
          </p>
        )}
        {state.errors.length > 0 && <ErrorList errors={state.errors} />}
      </div>
      <div className='mt-6 flex flex-wrap justify-end gap-3'>
        {state.backup && (
          <Button.SecondaryLG
            text='Backup erneut herunterladen'
            onClick={() => downloadJson(state.backup)}
          />
        )}
        {(isPaused || isFailed) && (
          <Button.SecondaryLG text='Schließen' onClick={close} />
        )}
        {isPaused && (
          <Button.DangerLG text='Fortsetzen' onClick={resumeDeletion} />
        )}
        {isFailed && (
          <Button.DangerLG text='Erneut versuchen' onClick={resumeDeletion} />
        )}
        {state.phase === 'done' && (
          <Button.PrimaryLG text='Zur Kundenliste' onClick={leaveToList} />
        )}
      </div>
    </div>
  )
}

// Äußere Hülle: headlessui unmountet die Children bei open=false, jeder
// Öffnungsvorgang startet also mit frischem State und lädt die Vorschau neu.
export default function DeletePartnerModal({ open, setOpen, partner }) {
  const [locked, setLocked] = useState(false)

  return (
    <Modal open={open} setOpen={setOpen} size='2xl' disableClose={locked}>
      {partner && (
        <DeletePartnerFlow
          partner={partner}
          close={() => setOpen(false)}
          onLockChange={setLocked}
        />
      )}
    </Modal>
  )
}
